import React, { useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Header from "../components/header";
import Footer from "../components/footer";
import Stepper from "../components/stepper";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import stripeStore from "../../store/stripe";
import mainStore from "../../store/store";
import sendBookingEmail from "./email";
import { useNavigate } from "react-router-dom";
import Summary from "../components/summary";
import { useEffect } from "react";
import { trackBookingStep, trackError } from "../../utils/analytics";

const CheckoutForm = () => {
  // useEffect(() => {
  //   const observer = new MutationObserver(() => {
  //     const offsetContainer = document.querySelector(".OffsetContainer");
  //     if (offsetContainer) {
  //       offsetContainer.style.display = "none";
  //       observer.disconnect(); // Stop observing once found
  //     }
  //   });

  //   observer.observe(document.body, { childList: true, subtree: true });

  //   return () => observer.disconnect();
  // }, []);
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const stripeRef = useRef(null);
  const { createPaymentIntent } = stripeStore();
  const { userBooking, createBooking, setUserBooking } = mainStore();
  const { checkCarAvailability, createVehicleHold, releaseVehicleHold, extendVehicleHold } = mainStore();
  console.log("CheckoutForm ~ userBooking:", userBooking);
  const navigate = useNavigate();

  // Generate a unique session ID for this checkout session
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [holdActive, setHoldActive] = useState(false);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);

  // 🔒 TEMPORARY HOLD: Create hold when entering checkout, release on leave
  useEffect(() => {
    const carId = userBooking.car?._id;
    const pickUpDate = userBooking.booking?.pickUpDate;
    const returnDate = userBooking.booking?.returnDate;

    if (!carId || !pickUpDate || !returnDate) return;

    // Create hold
    const createHold = async () => {
      console.log('🔒 Creating temporary hold for checkout...');
      const result = await createVehicleHold(carId, pickUpDate, returnDate, sessionId);
      if (result?.success) {
        setHoldActive(true);
        setHoldExpiresAt(new Date(result.hold.expiresAt));
        console.log('✅ Hold created, expires at:', result.hold.expiresAt);
      }
    };

    createHold();

    // Auto-extend hold every 8 minutes (hold lasts 10 min, extend before it expires)
    const extendInterval = setInterval(async () => {
      if (holdActive) {
        const result = await extendVehicleHold(sessionId);
        if (result?.success) {
          setHoldExpiresAt(new Date(result.expiresAt));
          console.log('🔄 Hold extended, new expiry:', result.expiresAt);
        }
      }
    }, 8 * 60 * 1000); // Every 8 minutes

    // Release hold when leaving the page
    return () => {
      clearInterval(extendInterval);
      console.log('🔓 Releasing hold (leaving checkout)...');
      releaseVehicleHold(sessionId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    address: Yup.string().required("Address is required"),
    contact: Yup.string()
      .matches(/^\d+$/, "Contact number must be digits only")
      .min(10, "Contact number must be at least 10 digits")
      .required("Contact is required"),
  });
  const initialValues = userBooking.customer;
  const AirPortAmount = useMemo(() => {
    let airportCharges = 0;
    if (userBooking?.booking?.pickUpLocation?.toLowerCase().includes("air")) {
      airportCharges += 50;
    }
    if (userBooking?.booking?.returnLocation?.toLowerCase().includes("air")) {
      airportCharges += 50;
    }
    return airportCharges;
  }, [userBooking]);
  const calculateExtra = useMemo(() => {
    return userBooking.extras.reduce((acc, extra) => {
      const isMultiplierApplicable =
        extra.title === "Damage Waiver" || extra.title === "Child Seat";
      const price = isMultiplierApplicable
        ? extra.price * (userBooking.days || 1)
        : extra.price;
      return acc + price;
    }, 0);
  }, [userBooking]);

  const calculateRate = useMemo(() => {
    return userBooking.rate.rate * (userBooking.days || 1) + AirPortAmount;
  }, [userBooking]);
  const taxAmount = useMemo(() => {
    return (calculateRate + parseFloat(calculateExtra)) * 0.1;
  }, [userBooking]);
  const discountAmount = useMemo(() => {
    return ((calculateRate + parseFloat(calculateExtra)) * 0.01).toFixed(2);
  }, [userBooking]);
  const totalPayment = useMemo(() => {
    return calculateRate + parseFloat(calculateExtra) + taxAmount;
  }, [userBooking]);

  const safeTrackBookingStep = (step, data = {}) => {
    try {
      trackBookingStep(step, data);
    } catch (analyticsError) {
      console.error("Analytics tracking failed:", analyticsError);
    }
  };

  const safeTrackError = (type, message, context = {}) => {
    try {
      trackError(type, message, context);
    } catch (analyticsError) {
      console.error("Analytics error tracking failed:", analyticsError);
    }
  };

  const sendEmailsSafely = async (bookingData, bookingReference) => {
    try {
      await sendBookingEmail(
        bookingData,
        calculateRate,
        totalPayment,
        discountAmount,
        taxAmount,
        calculateExtra,
        bookingReference
      );
    } catch (emailError) {
      console.error("Email sending failed:", emailError);
    }
  };

  // 🔒 PRE-CHECKOUT AVAILABILITY CHECK
  // Verifies the car is still available before processing payment
  // Prevents the scenario where two customers book the last unit simultaneously
  const verifyAvailabilityBeforePayment = async () => {
    const carId = userBooking.car?._id;
    const startDate = userBooking.booking?.pickUpDate;
    const endDate = userBooking.booking?.returnDate;

    if (!carId || !startDate || !endDate) {
      console.warn('⚠️ Missing car/date info for availability check, proceeding anyway');
      return true; // Allow booking to proceed if we can't check
    }

    try {
      console.log('🔒 Pre-checkout: Verifying car availability...');
      // Pass sessionId to exclude our own hold from the count
      const availability = await checkCarAvailability(carId, startDate, endDate, sessionId);
      
      if (availability && availability.available === false) {
        console.log('❌ Pre-checkout: Car is no longer available!');
        safeTrackError('availability_check_failed', 'Car no longer available at checkout', {
          carId,
          carName: userBooking.car?.name,
          startDate,
          endDate
        });
        return false;
      }

      console.log('✅ Pre-checkout: Car is still available');
      return true;
    } catch (error) {
      console.warn('⚠️ Availability check failed, proceeding with booking:', error.message);
      // If the check fails (network error etc.), allow the booking to proceed
      // The backend will still do its own conflict check
      return true;
    }
  };

  const payAtCounter = async () => {
    console.log('🔵 WEBSITE BOOKING - Pay at Counter clicked');
    console.log('📦 Booking data being sent:', userBooking);
    
    setLoading(true);

    // 🔒 Re-check availability before proceeding
    const isStillAvailable = await verifyAvailabilityBeforePayment();
    if (!isStillAvailable) {
      setLoading(false);
      alert(
        `Sorry, ${userBooking.car?.name || 'this vehicle'} is no longer available for your selected dates.\n\n` +
        `Another customer just booked it. Please go back and choose different dates or another vehicle.`
      );
      navigate("/cars");
      return;
    }

    // Track checkout started
    safeTrackBookingStep('checkout_started', {
      paymentMethod: 'counter',
      totalAmount: totalPayment,
      carId: userBooking.car?._id,
      carName: userBooking.car?.name,
      rentalDays: userBooking.days
    });
    
    try {
      console.log('📤 Calling createBooking API...');
      const result = await createBooking(userBooking);
      console.log('✅ Booking API response:', result);
      
      if (result) {
        console.log('✅ Booking created successfully! Reference:', result.bookingReference);

        // Release the hold since booking is confirmed
        releaseVehicleHold(sessionId);

        // If it was a timeout, booking was saved but we don't have the reference
        if (result._timedOut) {
          console.warn('⚠️ Booking likely created but response timed out');
          navigate("/thank-you");
          return;
        }

        sendEmailsSafely(userBooking, result.bookingReference);
        
        // Track booking completed
        safeTrackBookingStep('booking_completed', {
          bookingId: result._id,
          bookingReference: result.bookingReference,
          totalAmount: totalPayment,
          paymentMethod: 'counter',
          carId: userBooking.car?._id,
          carName: userBooking.car?.name
        });
        
        navigate("/thank-you");
      } else {
        console.error('❌ Booking API returned null/undefined');
        safeTrackError('booking_creation_failed', 'API returned null');
        alert('Booking failed. Please try again or contact support.');
      }
    } catch (error) {
      console.error('❌ Error creating booking:', error);
      safeTrackError('booking_creation_error', error.message);
      alert('Booking failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async (values) => {
    console.log("handleSubmit ~ e:", values);
    // e.preventDefault();

    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);

    setLoading(true);

    // 🔒 Re-check availability before processing card payment
    const isStillAvailable = await verifyAvailabilityBeforePayment();
    if (!isStillAvailable) {
      setLoading(false);
      alert(
        `Sorry, ${userBooking.car?.name || 'this vehicle'} is no longer available for your selected dates.\n\n` +
        `Another customer just booked it. Please go back and choose different dates or another vehicle.`
      );
      navigate("/cars");
      return;
    }
    
    // Track checkout started
    safeTrackBookingStep('checkout_started', {
      paymentMethod: 'card',
      totalAmount: totalPayment - discountAmount,
      carId: userBooking.car?._id,
      carName: userBooking.car?.name,
      rentalDays: userBooking.days
    });
    
    // Track payment attempt
    safeTrackBookingStep('payment_initiated', {
      amount: totalPayment - discountAmount,
      paymentMethod: 'card',
      hasDiscount: discountAmount > 0
    });

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          email: values.email,
          phone: values.contact,
          name: `${values.firstName} ${values.lastName}`,
        },
      });

      if (!error) {
        try {
          const res = await createPaymentIntent({
            amount: (totalPayment - discountAmount) * 100,
            info: paymentMethod,
          });

          if (res) {
            console.log('🔵 WEBSITE BOOKING - Card payment successful');
            console.log('📦 Booking data being sent:', userBooking);
            
            // Track successful payment
            safeTrackBookingStep('payment_success', {
              amount: totalPayment - discountAmount,
              bookingReference: res.bookingReference
            });

            console.log('📤 Calling createBooking API...');
            const bookingResult = await createBooking(userBooking);
            console.log('✅ Booking API response:', bookingResult);
            
            if (bookingResult) {
              console.log('✅ Booking created successfully! Reference:', bookingResult.bookingReference);

              // Release the hold since booking is confirmed
              releaseVehicleHold(sessionId);

              sendEmailsSafely(userBooking, bookingResult.bookingReference);
              
              // Track booking completion
              safeTrackBookingStep('booking_completed', {
                bookingId: bookingResult._id,
                bookingReference: bookingResult.bookingReference,
                carId: userBooking.car._id,
                carName: userBooking.car.name,
                totalAmount: totalPayment - discountAmount,
                rentalDays: userBooking.days
              });
              
              navigate("/thank-you");
            } else {
              console.error('❌ Booking API returned null/undefined');
              safeTrackError('booking_api_error', 'Booking API returned null');
              alert('Booking saved but there was an issue. Please contact support with your payment confirmation.');
              navigate("/thank-you");
            }
          }
        } catch (error) {
          safeTrackError('payment_processing_error', error.message);
          setLoading(false);
        }
      } else {
        safeTrackError('stripe_error', error.message);
        stripeRef.current.scrollIntoView({ behavior: "smooth" });
        setLoading(false);
      }
    } catch (error) {
      safeTrackError('payment_error', error.message);
      setLoading(false);
    }

    setLoading(false);
  };
  const cardElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#32325d",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
    },
    autocomplete: "off",
  };
  return (
    <>
      <Header />
      <section class="MainSection">
        <div class="container">
          <h1 class="Head_2">Book Your Car</h1>
        </div>
      </section>
      <section class="BookingSection">
        <div class="container">
          <Stepper />

          <div className="container py-4">
            <div className="row justify-content-center">
              {userBooking.payment_type == "card" ? (
                <div className="col-md-7">
                  <div className="card shadow">
                    <div
                      className="card-header text-white text-center"
                      style={{ backgroundColor: "#e02935" }}
                    >
                      <h4 style={{ color: "#fff" }} ref={stripeRef}>
                        Payment Form
                      </h4>
                    </div>
                    <div className="card-body">
                      <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={(values, { setSubmitting }) => {
                          handleSubmit(values); // Pass customer details and card data to your submit handler
                          setSubmitting(false);
                        }}
                      >
                        {({ isSubmitting, errors }) => (
                          <Form>
                            {/* First Name and Last Name */}
                            <div className="row mb-3">
                              <div
                                className={`input-container col-md-6 tooltip-wrapper ${
                                  errors.firstName ? "error" : ""
                                }`}
                                data-tooltip={errors.firstName}
                              >
                                <label
                                  htmlFor="firstName"
                                  className="form-label"
                                >
                                  First Name
                                </label>
                                <Field
                                  id="firstName"
                                  name="firstName"
                                  type="text"
                                  className="form-control"
                                />
                              </div>
                              <div
                                className={`input-container col-md-6 tooltip-wrapper ${
                                  errors.lastName ? "error" : ""
                                }`}
                                data-tooltip={errors.lastName}
                              >
                                <label
                                  htmlFor="lastName"
                                  className="form-label"
                                >
                                  Last Name
                                </label>
                                <Field
                                  id="lastName"
                                  name="lastName"
                                  type="text"
                                  className="form-control"
                                />
                              </div>
                            </div>

                            {/* Email and Address */}
                            <div className="row mb-3">
                              <div
                                className={`input-container col-md-6 tooltip-wrapper ${
                                  errors.email ? "error" : ""
                                }`}
                                data-tooltip={errors.email}
                              >
                                <label htmlFor="email" className="form-label">
                                  Email
                                </label>
                                <Field
                                  id="email"
                                  name="email"
                                  type="email"
                                  className="form-control"
                                />
                              </div>
                              <div
                                className={`input-container col-md-6 tooltip-wrapper ${
                                  errors.contact ? "error" : ""
                                }`}
                                data-tooltip={errors.contact}
                              >
                                {/* <label htmlFor="address" className="form-label">
                                Address
                              </label>
                              <Field
                                id="address"
                                name="address"
                                type="text"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="address"
                                component="div"
                                className="text-danger"
                              /> */}
                                <label htmlFor="contact" className="form-label">
                                  Contact
                                </label>
                                <Field
                                  id="contact"
                                  name="contact"
                                  type="text"
                                  className="form-control"
                                />
                              </div>
                            </div>

                            {/* Contact */}
                            {/* <div className="mb-3"></div> */}

                            {/* Stripe Card Elements Section */}
                            <div className="mb-3">
                              <label
                                htmlFor="cardNumber"
                                className="form-label"
                              >
                                Card Number
                              </label>
                              <div class="cardField">
                                <CardNumberElement
                                  id="cardNumber"
                                  className="form-control-card"
                                />
                                <div class="cardBrand">
                                  <img src="img/icon-visa.png"></img>
                                  <img src="img/icon-mastercard.png"></img>
                                  <img src="img/icon-amex.png"></img>
                                </div>
                              </div>
                            </div>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label
                                  htmlFor="cardExpiry"
                                  className="form-label"
                                >
                                  Expiry
                                </label>
                                <CardExpiryElement
                                  id="cardExpiry"
                                  className="form-control"
                                />
                              </div>
                              <div className="col-md-6">
                                <label htmlFor="cardCvc" className="form-label">
                                  CVC
                                </label>
                                <CardCvcElement
                                  id="cardCvc"
                                  className="form-control"
                                />
                              </div>
                            </div>

                            {error && (
                              <div className="alert alert-danger mt-3">
                                {error}
                              </div>
                            )}

                            {/* Submit Button */}
                            <button
                              data-analytics-name="Complete Booking - Card Payment"
                              type="submit"
                              className="BtnFill w-100 mt-4"
                              disabled={loading || !stripe || isSubmitting}
                            >
                              {loading ? "Processing..." : "Pay"}
                            </button>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="col-md-4">
                  <div className="card shadow">
                    <div
                      className="card-header text-white text-center"
                      style={{ backgroundColor: "#FFA500" }}
                    >
                      <h4 style={{ color: "#fff" }}>Pay at Counter</h4>
                    </div>
                    <div className="card-body text-center">
                      <p>No online payment required.</p>
                      <p>Pay when you pick up the service.</p>
                      <button
                        data-analytics-name="Complete Booking - Pay at Counter"
                        type="button"
                        className="BtnFill w-100 mt-4"
                        onClick={() => payAtCounter()}
                        disabled={loading}
                      >
                        {loading ? "Processing..." : "Proceed"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="col-md-5">
                <Summary />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CheckoutForm;
